import axios from "axios"

import { IAuthContext } from "@izettle/react-spa"

import { config } from "@/config"
import { logDatadogRumError } from "@/helpers"
import { LoaContract } from "@/models"

const sanitiseFile = (newFileName: string, file: File) => {
  const sanitisedFileName = `${newFileName}.${file.name.split(".").pop()}`
  const renamedFile = new File([file], sanitisedFileName, {
    type: file.type,
  })
  return renamedFile
}

export const fetchPurposes = async (getToken: IAuthContext["getToken"]): Promise<any> => {
  const accessToken = await getToken()

  try {
    const response = await axios({
      method: "get",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      url: `${config.documentUpload.url}/api/v2/document-purposes`,
    })
    return response.data
  } catch (error: any) {
    const errorMessage = new Error("failed API call - fetchPurposes")

    logDatadogRumError(errorMessage, error)
    return Promise.reject(errorMessage)
  }
}

export const uploadDocument = async (
  getToken: IAuthContext["getToken"],
  imageFile: any,
  documentPurpose: string,
  documentType: string,
  description?: string,
): Promise<any> => {
  const accessToken = await getToken()
  const formData = new FormData()
  formData.append("documentPurpose", documentPurpose)
  formData.append("documentType", documentType)
  formData.append("imageFile", sanitiseFile(documentPurpose, imageFile))
  if (description) {
    formData.append("description", description)
  }

  try {
    const response = await axios.post(
      `${config.documentUpload.url}/api/uploadDocument`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
      },
    )
    return response.status
  } catch (error: any) {
    const errorMessage = new Error("failed API call - uploadDocument")
    const errorData = {
      purpose: documentPurpose,
      type: documentType,
      description,
      image: {
        name: imageFile.name,
        type: imageFile.type,
      },
    }

    logDatadogRumError(errorMessage, error, errorData)
    return Promise.reject(errorMessage)
  }
}

export const uploadGenericDocuments = async (
  getToken: IAuthContext["getToken"],
  imageFiles: any,
): Promise<any> => {
  const accessToken = await getToken()

  const formData = new FormData()
  const imageDataForDatadogRum: any[] = []

  imageFiles.forEach((imageFile: any, index: number) => {
    formData.append(
      "imageFiles",
      sanitiseFile(`generic-doc-upload-img-${index}`, imageFile),
    )
    imageDataForDatadogRum.push({
      name: imageFile.name,
      type: imageFile.type,
    })
  })

  try {
    const response = await axios.post(
      `${config.documentUpload.url}/api/uploadGenericDocuments`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "Content-Type: multipart/form-data",
        },
      },
    )
    return response.status
  } catch (error: any) {
    const errorMessage = new Error("failed API call - uploadGenericDocuments")
    const errorData = {
      images: imageDataForDatadogRum,
    }

    logDatadogRumError(errorMessage, error, errorData)
    return Promise.reject(errorMessage)
  }
}

export const getRequirements = async (
  getToken: IAuthContext["getToken"],
): Promise<any> => {
  const accessToken = await getToken()

  try {
    const response = await axios({
      method: "get",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      url: `${config.documentUpload.url}/api/v2/generic-requirements`,
    })
    return response.data || []
  } catch (error: any) {
    const errorMessage = new Error("failed API call - getRequirements (Instructions)")

    logDatadogRumError(errorMessage, error)
    return Promise.reject(errorMessage)
  }
}

export const submitLoa = async (
  getToken: IAuthContext["getToken"],
  contract: LoaContract,
): Promise<any> => {
  const accessToken = await getToken()

  try {
    const response = await axios.post(
      `${config.documentUpload.url}/api/contract`,
      contract,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    )

    return response.status
  } catch (error: any) {
    const errorMessage = new Error("failed API call - submitLoa")

    logDatadogRumError(errorMessage, error)
    return Promise.reject(errorMessage)
  }
}
